import React, { useState, useEffect, useCallback } from 'react';
import UsersPage from './UsersPage';
import './AdminPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function AdminPage({ practices }) {
  const [activeTab, setActiveTab] = useState('users');

  return (
    <div className="admin-page">
      <h1 className="page-title">Admin</h1>
      <div className="detail-tabs">
        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        {/* Payments tab hidden on the Admin screen per David (2026-07-27). This
            button was the only entry point to PaymentsSection, so that section
            is now unreachable rather than deleted. Restore by reinstating:
              <button
                className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
                onClick={() => setActiveTab('payments')}
              >
                Payments
              </button>
            Unrelated to the client-facing Payments page in the sidebar
            (PaymentsPage.js), which is untouched. */}
      </div>

      <div className="detail-content">
        {activeTab === 'users' && <UsersPage practices={practices} />}
        {activeTab === 'payments' && <PaymentsSection />}
      </div>
    </div>
  );
}

function PaymentsSection() {
  const [practices, setPractices] = useState([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // Invoices from Stripe
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Single-practice month billing
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [billingMonth, setBillingMonth] = useState(defaultMonth);
  const [monthData, setMonthData] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);

  // Editable bill fields
  const [edits, setEdits] = useState({ manual_collections: 0, credentialing_hours: 0, credentialing_rate: 25 });
  const [savingBill, setSavingBill] = useState(false);
  const [invoicingId, setInvoicingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);

  // Inline contract editing
  const [configEditing, setConfigEditing] = useState(false);
  const [configForm, setConfigForm] = useState({ collections_fee_pct: '', monthly_minimum: '', stripe_customer_id: '' });
  const [savingConfig, setSavingConfig] = useState(false);

  const selectedPractice = practices.find(p => p.id === selectedPracticeId);

  const loadPractices = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/practices`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        const list = data.practices || [];
        setPractices(list);
        if (list.length > 0 && !selectedPracticeId) {
          setSelectedPracticeId(list[0].id);
        }
      } else {
        setMessage({ type: 'error', text: 'Failed to load billing practices' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to load billing practices' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    if (!selectedPracticeId) return;
    setInvoicesLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/practices/${selectedPracticeId}/invoices`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setInvoices(data.invoices || []);
      } else {
        setInvoices([]);
      }
    } catch {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, [selectedPracticeId]);

  const loadMonthData = useCallback(async () => {
    if (!selectedPracticeId) return;
    setMonthLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/practices/${selectedPracticeId}/month/${billingMonth}`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setMonthData(data);
        const b = data.billing;
        setEdits({
          manual_collections: b ? b.manual_collections : 0,
          credentialing_hours: b ? b.credentialing_hours : 0,
          credentialing_rate: b ? b.credentialing_rate : 25,
        });
      } else {
        setMonthData(null);
      }
    } catch {
      setMonthData(null);
    } finally {
      setMonthLoading(false);
    }
  }, [selectedPracticeId, billingMonth]);

  useEffect(() => { loadPractices(); }, [loadPractices]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);
  useEffect(() => { loadMonthData(); }, [loadMonthData]);

  // Reset config editing when practice changes
  useEffect(() => { setConfigEditing(false); }, [selectedPracticeId]);

  const changeMonth = (delta) => {
    const [y, m] = billingMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setBillingMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const startConfigEdit = () => {
    if (!selectedPractice) return;
    setConfigForm({
      collections_fee_pct: selectedPractice.collections_fee_pct ?? '',
      monthly_minimum: selectedPractice.monthly_minimum ?? '',
      stripe_customer_id: selectedPractice.stripe_customer_id || '',
    });
    setConfigEditing(true);
  };

  const saveConfig = async () => {
    if (!selectedPracticeId) return;
    setSavingConfig(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/practices/${selectedPracticeId}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          collections_fee_pct: configForm.collections_fee_pct !== '' ? parseFloat(configForm.collections_fee_pct) : null,
          monthly_minimum: configForm.monthly_minimum !== '' ? parseFloat(configForm.monthly_minimum) : null,
          stripe_customer_id: configForm.stripe_customer_id || null,
        }),
      });
      if (resp.ok) {
        setMessage({ type: 'success', text: 'Billing config updated' });
        setConfigEditing(false);
        loadPractices();
        loadInvoices();
        loadMonthData();
      } else {
        const err = await resp.json();
        setMessage({ type: 'error', text: err.error || 'Failed to save' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to save config' });
    } finally {
      setSavingConfig(false);
    }
  };

  const saveBilling = async () => {
    if (!selectedPracticeId) return;
    setSavingBill(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/month/${billingMonth}/practice/${selectedPracticeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          manual_collections: parseFloat(edits.manual_collections) || 0,
          credentialing_hours: parseFloat(edits.credentialing_hours) || 0,
          credentialing_rate: parseFloat(edits.credentialing_rate) || 25,
        }),
      });
      if (resp.ok) {
        setMessage({ type: 'success', text: 'Billing saved' });
        loadMonthData();
        loadInvoices();
      } else {
        const err = await resp.json();
        setMessage({ type: 'error', text: err.error || 'Failed to save' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to save billing' });
    } finally {
      setSavingBill(false);
    }
  };

  const sendInvoice = async (billingId) => {
    setInvoicingId(billingId);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/${billingId}/send-invoice`, {
        method: 'POST',
        credentials: 'include',
      });
      if (resp.ok) {
        setMessage({ type: 'success', text: 'Invoice sent' });
        loadMonthData();
        loadInvoices();
      } else {
        const err = await resp.json();
        setMessage({ type: 'error', text: err.error || 'Failed to send invoice' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to send invoice' });
    } finally {
      setInvoicingId(null);
    }
  };

  const syncStatus = async (billingId) => {
    setSyncingId(billingId);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/${billingId}/sync-status`, {
        method: 'POST',
        credentials: 'include',
      });
      if (resp.ok) {
        setMessage({ type: 'success', text: 'Invoice status synced' });
        loadMonthData();
        loadInvoices();
      } else {
        const err = await resp.json();
        setMessage({ type: 'error', text: err.error || 'Failed to sync' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to sync status' });
    } finally {
      setSyncingId(null);
    }
  };

  const fmt = (n) => {
    if (n == null) return '-';
    return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMonth = (monthStr) => {
    const [y, m] = monthStr.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  };

  if (loading) return <div className="loading-state"><div className="spinner"></div>Loading...</div>;

  // Compute bill line items from monthData
  const b = monthData?.billing;
  const eraAmt = b ? b.era_collections : (monthData?.era_collections || 0);
  const manualAmt = parseFloat(edits.manual_collections) || 0;
  const totalCol = eraAmt + manualAmt;
  const feePct = selectedPractice?.collections_fee_pct;
  const minimum = selectedPractice?.monthly_minimum || 0;
  const pctFee = feePct != null ? totalCol * feePct / 100 : 0;
  const collFee = Math.max(pctFee, minimum);
  const credHrs = parseFloat(edits.credentialing_hours) || 0;
  const credRate = parseFloat(edits.credentialing_rate) || 25;
  const credFee = credHrs * credRate;
  const totalFee = collFee + credFee;
  const invoiceStatus = b ? b.invoice_status : 'draft';
  const isLocked = b && b.invoice_status !== 'draft';

  return (
    <div className="payments-section">
      {message && (
        <div className={`form-message ${message.type} dismissable`}>
          <span className="message-content">{message.text}</span>
          <button className="message-close" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {/* Practice Dropdown */}
      <div className="payments-practice-selector">
        <select
          value={selectedPracticeId || ''}
          onChange={(e) => setSelectedPracticeId(parseInt(e.target.value, 10))}
        >
          {practices.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {selectedPractice && (
        <>
          {/* Top Row: Contract Details + Invoices */}
          <div className="payments-top-row">
            {/* Left: Contract Details */}
            <div className="payments-panel">
              <div className="payments-panel-header">
                <h4>Contract Details</h4>
                {!configEditing && (
                  <button className="btn btn-secondary btn-sm" onClick={startConfigEdit}>Edit</button>
                )}
              </div>
              <div className="payments-panel-body">
                {configEditing ? (
                  <>
                    <table className="contract-details-table">
                      <tbody>
                        <tr>
                          <td>Collections Rate</td>
                          <td>
                            <input
                              type="number" min="0" max="100" step="0.01"
                              className="inline-input"
                              value={configForm.collections_fee_pct}
                              onChange={(e) => setConfigForm(f => ({ ...f, collections_fee_pct: e.target.value }))}
                              placeholder="e.g. 7.5"
                            /> %
                          </td>
                        </tr>
                        <tr>
                          <td>Monthly Minimum</td>
                          <td>
                            $ <input
                              type="number" min="0" step="0.01"
                              className="inline-input"
                              value={configForm.monthly_minimum}
                              onChange={(e) => setConfigForm(f => ({ ...f, monthly_minimum: e.target.value }))}
                              placeholder="e.g. 500"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>Stripe Customer</td>
                          <td>
                            <input
                              type="text"
                              className="inline-input"
                              style={{ width: '100%' }}
                              value={configForm.stripe_customer_id}
                              onChange={(e) => setConfigForm(f => ({ ...f, stripe_customer_id: e.target.value }))}
                              placeholder="cus_..."
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="contract-edit-actions">
                      <button className="btn btn-primary btn-sm" onClick={saveConfig} disabled={savingConfig}>
                        {savingConfig ? 'Saving...' : 'Save'}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setConfigEditing(false)}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <table className="contract-details-table">
                    <tbody>
                      <tr>
                        <td>Collections Rate</td>
                        <td>{selectedPractice.collections_fee_pct != null ? `${selectedPractice.collections_fee_pct}%` : <span className="text-muted">Not set</span>}</td>
                      </tr>
                      <tr>
                        <td>Monthly Minimum</td>
                        <td>{selectedPractice.monthly_minimum != null ? fmt(selectedPractice.monthly_minimum) : <span className="text-muted">Not set</span>}</td>
                      </tr>
                      <tr>
                        <td>Stripe Customer</td>
                        <td>{selectedPractice.stripe_customer_id ? <span title={selectedPractice.stripe_customer_id}>{selectedPractice.stripe_customer_id}</span> : <span className="text-muted">Not configured</span>}</td>
                      </tr>
                      <tr>
                        <td>Clearinghouse</td>
                        <td>{selectedPractice.clearinghouse || <span className="text-muted">None</span>}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Right: Invoices */}
            <div className="payments-panel">
              <div className="payments-panel-header">
                <h4>Payments / Invoices</h4>
              </div>
              {!selectedPractice.stripe_customer_id ? (
                <div className="invoices-empty">No Stripe customer configured</div>
              ) : invoicesLoading ? (
                <div className="invoices-empty"><div className="spinner"></div> Loading invoices...</div>
              ) : invoices.length === 0 ? (
                <div className="invoices-empty">No invoices found</div>
              ) : (
                <div className="payments-invoices-scroll">
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(inv => (
                        <tr key={inv.id}>
                          <td>{new Date(inv.created).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</td>
                          <td className="col-currency">{fmt(inv.amount_due)}</td>
                          <td><span className={`invoice-badge ${inv.status}`}>{inv.status}</span></td>
                          <td>
                            {inv.hosted_invoice_url && (
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="btn-link" style={{ fontSize: 12 }}>
                                View
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* New Bill */}
          <div className="payments-new-bill">
            <div className="payments-new-bill-header">
              <h4>New Bill &mdash; {formatMonth(billingMonth)}</h4>
              <div className="month-selector">
                <button className="btn btn-secondary btn-sm" onClick={() => changeMonth(-1)}>&larr;</button>
                <input
                  type="month"
                  value={billingMonth}
                  onChange={(e) => setBillingMonth(e.target.value)}
                />
                <button className="btn btn-secondary btn-sm" onClick={() => changeMonth(1)}>&rarr;</button>
              </div>
            </div>
            {monthLoading ? (
              <div className="payments-new-bill-body">
                <div className="loading-state"><div className="spinner"></div>Loading...</div>
              </div>
            ) : (
              <div className="payments-new-bill-body">
                <div className="bill-summary">
                  {/* Left column: Collections */}
                  <div>
                    <div className="bill-line">
                      <span className="bill-line-label">Automated ERA Collections</span>
                      <span className="bill-line-value">{fmt(eraAmt)}</span>
                    </div>
                    <div className="bill-line">
                      <span className="bill-line-label">+ Additional</span>
                      <span className="bill-line-input">
                        <input
                          type="number" className="inline-input" min="0" step="0.01"
                          value={edits.manual_collections}
                          onChange={(e) => setEdits(prev => ({ ...prev, manual_collections: e.target.value }))}
                          disabled={isLocked}
                        />
                      </span>
                    </div>
                    <div className="bill-line">
                      <span className="bill-line-label">= Total Collections</span>
                      <span className="bill-line-value"><strong>{fmt(totalCol)}</strong></span>
                    </div>
                  </div>

                  {/* Right column: Fees */}
                  <div>
                    <div className="bill-line">
                      <span className="bill-line-label">Collections Fee</span>
                      <span className="bill-line-value">
                        {fmt(b ? b.collections_fee : collFee)}
                        {feePct != null && (
                          <span className="fee-breakdown" style={{ marginLeft: 8 }}>
                            {feePct}% x {fmt(totalCol)}{minimum > 0 ? `, min ${fmt(minimum)}` : ''}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="bill-line">
                      <span className="bill-line-label">Cred Hours</span>
                      <span className="bill-line-input">
                        <input
                          type="number" className="inline-input inline-input-sm" min="0" step="0.25"
                          value={edits.credentialing_hours}
                          onChange={(e) => setEdits(prev => ({ ...prev, credentialing_hours: e.target.value }))}
                          disabled={isLocked}
                        />
                        <span className="fee-breakdown">x ${credRate}/hr = {fmt(b ? b.credentialing_fee : credFee)}</span>
                      </span>
                    </div>
                    <div className="bill-line">
                      <span className="bill-line-label">Total Fee</span>
                      <span className="bill-line-value total"><strong>{fmt(b ? b.total_fee : totalFee)}</strong></span>
                    </div>
                  </div>
                </div>

                {/* Status + Actions */}
                <div className="bill-actions">
                  <span className={`invoice-badge ${invoiceStatus}`} style={{ alignSelf: 'center' }}>
                    {invoiceStatus}
                  </span>
                  {!isLocked && (
                    <button className="btn btn-primary btn-sm" onClick={saveBilling} disabled={savingBill}>
                      {savingBill ? 'Saving...' : 'Save Draft'}
                    </button>
                  )}
                  {b && b.id && invoiceStatus === 'draft' && selectedPractice.stripe_customer_id && !b.stripe_invoice_id && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => sendInvoice(b.id)}
                      disabled={invoicingId === b.id}
                    >
                      {invoicingId === b.id ? 'Creating...' : 'Create Stripe Draft'}
                    </button>
                  )}
                  {b && b.stripe_invoice_id && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => syncStatus(b.id)}
                      disabled={syncingId === b.id}
                    >
                      {syncingId === b.id ? 'Syncing...' : 'Sync Status'}
                    </button>
                  )}
                  {b && b.stripe_invoice_url && (
                    <a href={b.stripe_invoice_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>
                      View Invoice
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminPage;
